import { MenuRow } from '@repo/studio-components-menu-row'
import type { AppSwitchBarItem } from '@repo/studio-components/top-app-bar/components/app-switch-bar'
import { useIsCompactViewport } from '@repo/studio-hooks-dom/use-breakpoint'
import * as Popover from 'primitives/popover'
import * as React from 'react'
import { tw } from 'utils/classnames'
import { NavigationItem } from './navigation-item'
import type { NavigationClusterId, NavigationOnlyItem } from './navigation-cluster-data'
import { useClusterDefinitions } from './navigation-cluster-data'
import { StudioLayoutLink } from '@repo/studio-config/studio-config-router'

interface NavigationClusterMenuItemBaseProps {
  label: string
  icon: React.ReactNode
  showLabel: boolean
  isOpen?: boolean
}

// Cluster rows are stacked with `gap-1` (see `NavigationSection`), so 4px of dead space sits
// between two hover triggers. Crossing it leaves no trigger hovered, which closes the open flyout
// before the next row can open its own — the pointer has to traverse the whole gap first. Two 2px
// hitboxes stretch each row's hover area into half of the gap above and below so neighbouring rows
// meet, the same trick `Popover.Content` uses to bridge its `sideOffset` gap to the trigger.
const rowGapHitbox = tw(
  'relative',
  'before:absolute before:inset-x-0 before:-top-0.5 before:h-0.5 before:content-[""]',
  'after:absolute after:inset-x-0 after:-bottom-0.5 after:h-0.5 after:content-[""]',
)

function NavigationClusterMenuItemContent({
  label,
  icon,
  showLabel,
  isOpen,
}: {
  label: string
  icon: React.ReactNode
  showLabel: boolean
  isOpen?: boolean
}) {
  return (
    <>
      <MenuRow.Icon>{icon}</MenuRow.Icon>
      {showLabel ? (
        <>
          <MenuRow.Label>{label}</MenuRow.Label>
          <MenuRow.Indicator className={tw(isOpen && 'translate-x-1')} />
        </>
      ) : null}
    </>
  )
}

function NavigationClusterMenuItemLink({
  route,
  label,
  icon,
  showLabel,
  isOpen,
  className,
  ...props
}: NavigationClusterMenuItemBaseProps & {
  route: string
} & React.ComponentProps<typeof MenuRow>) {
  return (
    <MenuRow
      asChild
      variant="slim"
      aria-expanded={isOpen}
      aria-haspopup="true"
      className={tw(rowGapHitbox, className)}
      {...props}
    >
      <MenuRow.Link asChild>
        <StudioLayoutLink to={route}>
          <NavigationClusterMenuItemContent
            label={label}
            icon={icon}
            showLabel={showLabel}
            isOpen={isOpen}
          />
        </StudioLayoutLink>
      </MenuRow.Link>
    </MenuRow>
  )
}

function NavigationClusterMenuItemButton({
  label,
  icon,
  showLabel,
  onClick,
  isOpen,
  className,
  ...props
}: NavigationClusterMenuItemBaseProps & {
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
} & React.ComponentProps<typeof MenuRow>) {
  return (
    <MenuRow
      variant="slim"
      onClick={onClick}
      aria-expanded={isOpen}
      aria-haspopup="true"
      className={tw(rowGapHitbox, className)}
      {...props}
    >
      <NavigationClusterMenuItemContent
        label={label}
        icon={icon}
        showLabel={showLabel}
        isOpen={isOpen}
      />
    </MenuRow>
  )
}

/**
 * A cluster row that goes somewhere instead of opening tools: it draws no chevron,
 * makes no menu promise to assistive technology and has no popover around it.
 */
export function NavigationClusterLink({
  route,
  label,
  icon,
  showLabel,
}: {
  route: string
  label: string
  icon: React.ReactNode
  showLabel: boolean
}) {
  return (
    <MenuRow asChild variant="slim">
      <MenuRow.Link asChild>
        <StudioLayoutLink to={route}>
          <MenuRow.Icon>{icon}</MenuRow.Icon>
          {showLabel ? <MenuRow.Label>{label}</MenuRow.Label> : null}
        </StudioLayoutLink>
      </MenuRow.Link>
    </MenuRow>
  )
}

export const NavigationClusterMenuItem = {
  Link: NavigationClusterMenuItemLink,
  Button: NavigationClusterMenuItemButton,
}

export function NavigationCluster({
  cluster,
  items,
  expanded,
  onNavigate,
  route,
  navigationOnlyItems = [],
}: {
  cluster: NavigationClusterId
  items: AppSwitchBarItem[]
  expanded: boolean
  onNavigate?: () => void
  route?: string
  /** Extra items shown in the popover that don't have a backend ToolType (no favorite button). */
  navigationOnlyItems?: NavigationOnlyItem[]
}) {
  const definitions = useClusterDefinitions()
  const definition = definitions.find(definition => definition.cluster === cluster)

  if (!definition) {
    return null
  }
  const { label, icon, disabled, disabledMessage } = definition
  const destination = route ?? definition.route

  return (
    <NavigationClusterPopover
      label={label}
      icon={icon}
      route={destination}
      expanded={expanded}
      // top border + bottom border + top padding + margin top of navigation + title height
      alignOffset={-(1 + 1 + 16 + 8 + 24)}
    >
      <div className="w-full border-b border-gray-900 pb-1">
        <span className="ml-2 typography-12 text-gray-500">{label}</span>
      </div>
      {disabled ? (
        <div className="ml-2 py-2 typography-12 text-gray-500">{disabledMessage}</div>
      ) : (
        // Tools and nav-only items share one list ordered by `order`. A tool (has a
        // ToolType) also renders the favorite button; a nav-only item does not.
        [...items, ...navigationOnlyItems]
          .toSorted((a, b) => a.order - b.order)
          .map(item => (
            <NavigationItem
              key={item.title}
              title={item.title}
              route={item.route}
              expanded={true}
              icon={item.icon}
              onNavigate={onNavigate}
              tool={'toolType' in item ? item : undefined}
            />
          ))
      )}
    </NavigationClusterPopover>
  )
}

export function NavigationPopoverContent({
  children,
  alignOffset,
  onMouseEnter,
  onMouseLeave,
}: {
  children: React.ReactNode
  alignOffset: number
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}) {
  const isCompactViewport = useIsCompactViewport()
  const contentRef = React.useRef<HTMLDivElement | null>(null)

  return (
    <Popover.Content
      side={isCompactViewport ? 'top' : 'right'}
      align="start"
      sideOffset={8}
      alignOffset={alignOffset}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onOpenAutoFocus={e => e.preventDefault()}
      className="bg-gray-1000 rounded-4 shadow-md border border-gray-800 p-4 min-w-[200px] -translate-y-1"
    >
      <div ref={contentRef} className="flex flex-col gap-1 mt-2">
        {children}
      </div>
    </Popover.Content>
  )
}

export function NavigationClusterPopover({
  label,
  icon,
  route,
  expanded,
  alignOffset,
  children,
}: {
  label: string
  icon: React.ReactNode
  route?: string
  expanded: boolean
  alignOffset: number
  children: React.ReactNode
}) {
  const popover = Popover.usePopoverContext()
  return (
    <>
      <Popover.Trigger asChild>
        {route ? (
          <NavigationClusterMenuItem.Link
            route={route}
            label={label}
            icon={icon}
            showLabel={expanded}
            isOpen={popover.open}
          />
        ) : (
          <NavigationClusterMenuItem.Button
            label={label}
            icon={icon}
            showLabel={expanded}
            isOpen={popover.open}
          />
        )}
      </Popover.Trigger>

      <Popover.Portal>
        <NavigationPopoverContent alignOffset={alignOffset}>{children}</NavigationPopoverContent>
      </Popover.Portal>
    </>
  )
}
